import { useContext, useState } from "react";
import { isEmpty, values } from "lodash";
import { ArrowRightIcon, DoughnutChartIcon } from "evergreen-ui";
import { css } from "glamor";
import {
  Button,
  Heading,
  IconButton,
  Pane,
  Paragraph,
  ConfigureButtons,
  EmptyCard,
} from "components/materials";
import { majorScale, minorScale, ThemeContext } from "helpers/utilities";
import { DonutChart } from "./DonutChart";
import { DonutChartLegend } from "./DonutChartLegend";
import { contingencySegmentData } from "./chartHelpers";

export const CONTINGENCY_USAGE_CONFIGURATION_SETTINGS = {
  i: "contingencyUsage",
  x: 1,
  y: 3,
  w: 1,
  h: 1,
  disabled: false,
};

export function ContingencyUsage({
  history,
  isConfigurable,
  isDisabled,
  project,
  cards,
  setCards,
  name,
}) {
  const theme = useContext(ThemeContext);
  const [filterByHardCosts, setFilterByHardCosts] = useState(true);

  const { budgetAdjustments } = project;
  const lineItems = project.divisions.flatMap((division) => division.lineItems);

  const prepareContingencyData = (lineItems, budgetAdjustments) => {
    const preparedLineItemsMap = lineItems.reduce((acc, lineItem) => {
      if ((filterByHardCosts && lineItem.hardCosts) || !filterByHardCosts) {
        return { ...acc, [lineItem.id]: lineItem };
      }
      return acc;
    }, {});

    const preparedBudgetAdjustments = budgetAdjustments.reduce(
      (acc, adjustment) => {
        const { transactions } = adjustment;

        const filteredTransactions = transactions.filter(
          (transaction) => preparedLineItemsMap[transaction.lineItemId]
        );

        if (isEmpty(filteredTransactions)) return acc;

        return [...acc, { ...adjustment, transactions: filteredTransactions }];
      },
      []
    );

    return {
      preparedLineItems: values(preparedLineItemsMap),
      preparedBudgetAdjustments,
    };
  };

  const {
    preparedLineItems,
    preparedBudgetAdjustments,
  } = prepareContingencyData(lineItems, budgetAdjustments);

  const {
    allContingencySegments,
    percentUsed,
    originalContingency,
    untrackedContingency,
  } = contingencySegmentData({
    budgetAdjustments: preparedBudgetAdjustments,
    lineItems: preparedLineItems,
  });

  const noContingencies =
    allContingencySegments.length === 1 &&
    allContingencySegments[0].name === "Remaining Contingency" &&
    allContingencySegments[0].value === 0;

  if (noContingencies) {
    return (
      <EmptyCard
        name="Contingency Usage"
        isConfigurable={isConfigurable}
        isDisabled={isDisabled}
        cardName={name}
        cards={cards}
        setCards={setCards}
        icon={<DoughnutChartIcon color="muted" marginLeft={minorScale(3)} />}
      >
        <Paragraph paddingBottom={majorScale(1)}>
          This project does not have any contingency line items.
        </Paragraph>
        <Button
          marginTop={8}
          appearance="primary"
          onClick={() =>
            history.push(
              `/projects/${project.id}/settings?settings=lineItemSettings`
            )
          }
        >
          Set Up Contingency
        </Button>
      </EmptyCard>
    );
  }

  return (
    <Pane
      width="100%"
      height="100%"
      position="relative"
      padding={majorScale(2)}
    >
      <Pane
        id="header"
        display="flex"
        marginBottom={minorScale(3)}
        borderBottom={`1px solid ${theme.colors.gray100}`}
        paddingBottom={majorScale(2)}
        justifyContent="space-between"
        alignItems="center"
      >
        <Pane display="flex" alignItems="center">
          <Heading
            fontSize={majorScale(3)}
            lineHeight={1.2}
            fontWeight={500}
            color={isDisabled ? theme.colors.gray600 : null}
          >
            Contingency Usage
          </Heading>
          <DoughnutChartIcon color="muted" marginLeft={minorScale(3)} />
        </Pane>
        <Pane marginLeft="auto" marginRight={majorScale(1)}>
          <Button
            isActive={filterByHardCosts === true}
            id="filterHardCostsButton"
            appearance="minimal"
            {...css({
              borderRadius: 0,
              "&#filterHardCostsButton:focus": {
                boxShadow: "none",
              },
            })}
            onClick={() => setFilterByHardCosts(true)}
          >
            Hard Costs
          </Button>
          <Button
            isActive={filterByHardCosts === false}
            id="showAllCostsButton"
            appearance="minimal"
            {...css({
              borderRadius: 0,
              "&#showAllCostsButton:focus": {
                boxShadow: "none",
              },
            })}
            onClick={() => setFilterByHardCosts(false)}
          >
            Total Costs
          </Button>
        </Pane>
        {isConfigurable && (
          <ConfigureButtons
            isDisabled={isDisabled}
            cards={cards}
            setCards={setCards}
            name={name}
          />
        )}
        {!isConfigurable && (
          <IconButton
            icon={ArrowRightIcon}
            onClick={() => history.push(`/projects/${project.id}/budget`)}
            appearance="minimal"
            border={`1px solid ${theme.colors.gray400}`}
            marginLeft="auto"
          />
        )}
      </Pane>
      <Pane height="80%" width="100%" display="flex" paddingTop={majorScale(1)}>
        <DonutChart
          contingencySegments={allContingencySegments}
          percentUsed={percentUsed}
        />
        <DonutChartLegend
          contingencySegments={allContingencySegments}
          untrackedContingency={untrackedContingency}
          originalAmount={originalContingency}
        />
      </Pane>
    </Pane>
  );
}
